import React, { useEffect, useState } from 'react';
import { FileText, Image as ImageIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

function isImageFile(file: File): boolean {
  return (file.type || '').startsWith('image/');
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${Math.round(kb)} KB`;
  return `${(kb / 1024).toFixed(1)} MB`;
}

function fileTypeLabel(file: File): string {
  const name = file.name.toLowerCase();
  if (isImageFile(file)) return 'Image';
  if ((file.type || '').includes('pdf') || name.endsWith('.pdf')) return 'PDF';
  if (name.endsWith('.csv') || file.type === 'text/csv') return 'CSV';
  if (name.endsWith('.md') || name.endsWith('.markdown')) return 'Markdown';
  return 'File';
}

function useObjectUrl(file: File, enabled: boolean): string | null {
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled) {
      setUrl(null);
      return;
    }
    const objectUrl = URL.createObjectURL(file);
    setUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [enabled, file]);

  return url;
}

interface TemporaryAttachmentReceiptProps {
  files?: File[];
  className?: string;
  variant?: 'default' | 'on-primary';
}

const AttachmentReceiptItem: React.FC<{
  file: File;
  variant: 'default' | 'on-primary';
}> = ({ file, variant }) => {
  const previewUrl = useObjectUrl(file, isImageFile(file));
  const onPrimary = variant === 'on-primary';

  return (
    <div
      className={cn(
        'flex h-12 min-w-0 max-w-[260px] items-center gap-2 overflow-hidden rounded-xl border pr-3 text-xs',
        onPrimary
          ? 'border-primary-foreground/20 bg-primary-foreground/10 text-primary-foreground'
          : 'border-border bg-background text-foreground',
      )}
      title={file.name}
    >
      <div
        className={cn(
          'flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden',
          onPrimary ? 'bg-primary-foreground/10' : 'bg-muted',
        )}
      >
        {previewUrl ? (
          <img src={previewUrl} alt="" className="h-full w-full object-cover" draggable={false} />
        ) : (
          <span
            className={cn(
              'flex h-8 w-8 items-center justify-center rounded-lg',
              onPrimary
                ? 'bg-primary-foreground/10 text-primary-foreground'
                : 'bg-muted-foreground/10 text-muted-foreground',
            )}
          >
            {isImageFile(file) ? (
              <ImageIcon className="h-3.5 w-3.5" />
            ) : (
              <FileText className="h-3.5 w-3.5" />
            )}
          </span>
        )}
      </div>
      <div className="min-w-0">
        <div className="truncate font-medium">{file.name}</div>
        <div className={cn('truncate', onPrimary ? 'text-primary-foreground/70' : 'text-muted-foreground')}>
          {fileTypeLabel(file)} · {formatFileSize(file.size)}
        </div>
      </div>
    </div>
  );
};

export const TemporaryAttachmentReceipt: React.FC<TemporaryAttachmentReceiptProps> = ({
  files,
  className,
  variant = 'default',
}) => {
  if (!files?.length) return null;

  return (
    <div className={cn('flex flex-wrap gap-2', className)} aria-label="Attached files">
      {files.map((file, index) => (
        <AttachmentReceiptItem
          key={`${file.name}:${file.size}:${file.lastModified}:${index}`}
          file={file}
          variant={variant}
        />
      ))}
    </div>
  );
};
